import { Button } from '../../components/Button';
import { Counter } from '../../components/Counter';
import { ErrorText } from '../../components/ErrorText';
import { InfoNote } from '../../components/InfoNote';
import { NotificationBox } from '../../components/NotificationBox';
import { OptionRow } from '../../components/OptionRow';
import { PageHeading } from '../../components/PageHeading';
import { PanelCard } from '../../components/PanelCard';
import { ReservationCalendar } from '../../components/ReservationCalendar';
import { SectionHeader } from '../../components/SectionHeader';
import { TimeslotChipSelector } from '../../components/TimeslotChipSelector';
import { TimeChangeIcon } from '../../icons/TimeChangeIcon';
import { GuestDetailsPanel } from '../GuestDetailsPanel';
import {
  AddGuestRow,
  CardStack,
  EmptyNote,
  ErrorRow,
  GroupLabel,
  OptionList,
  OptionSection,
  PlayerLabel,
  PlayerRow,
  ScrollContainer,
  SectionRule,
} from './GolfBookingContent.styles';
import type { GolfBookingContentProps } from './GolfBookingContent.types';

const defaultNoteIcon = <TimeChangeIcon size={18} />;

export function GolfBookingContent({
  title,
  subtitle,
  unavailable,
  onUnavailableCtaClick,
  onBack,
  playerCount,
  minPlayers = 1,
  maxPlayers,
  onPlayerCountChange,
  activeDate,
  selectedDate,
  calendarView,
  availability,
  onDateSelect,
  onWeekNavigate,
  onCalendarViewChange,
  calendarLabels,
  holesOptions = [],
  selectedHoles,
  onHolesSelect,
  timeBands = [],
  selectedTimeBand,
  onTimeBandSelect,
  teeTimes = [],
  selectedTeeTime,
  onTeeTimeSelect,
  guests = [],
  existingGuests,
  onExistingGuestSelect,
  onGuestChange,
  titleOptions,
  onAddGuest,
  canAddGuest,
  errorMessage,
  noteText,
  noteIcon = defaultNoteIcon,
  labels,
}: GolfBookingContentProps) {
  const playersLabel = labels?.playersLabel ?? 'Guests';
  const holesLabel = labels?.holesLabel ?? 'Choose your round';

  const showHoles = holesOptions.length > 0 && !!onHolesSelect;
  const hasDate = !!selectedDate;
  const showTimeBands = hasDate && timeBands.length > 0 && !!onTimeBandSelect;
  const awaitingTimeBand = showTimeBands && !selectedTimeBand;
  const showTeeTimes = hasDate && !!onTeeTimeSelect && !awaitingTimeBand;
  const showTimeBandPrompt = awaitingTimeBand && !!onTeeTimeSelect;
  const offersTimes = (timeBands.length > 0 && !!onTimeBandSelect) || !!onTeeTimeSelect;
  const showDatePrompt = !hasDate && !unavailable && offersTimes;
  const selectedGuestIds = guests
    .map((guest) => guest.selectedGuestId)
    .filter((id): id is string => id !== undefined);

  const unavailableNotice = unavailable
    ? {
        title: labels?.unavailable?.title ?? 'Unavailable',
        subtitle: labels?.unavailable?.subtitle ?? 'No tee times are available for this selection.',
        ctaLabel: labels?.unavailable?.ctaLabel,
      }
    : undefined;

  return (
    <ScrollContainer>
      <PageHeading title={title} subtitle={subtitle} centered fullWidth />

      <PanelCard size="large">
        <CardStack>
          <SectionHeader
            title={labels?.reservation?.title ?? 'Reservation.'}
            subtitle={labels?.reservation?.subtitle}
          />

          <PlayerRow>
            <PlayerLabel>{playersLabel}</PlayerLabel>
            <Counter
              value={playerCount}
              min={minPlayers}
              max={maxPlayers}
              onChange={onPlayerCountChange}
              decreaseLabel={labels?.counter?.decrease}
              increaseLabel={labels?.counter?.increase}
            />
          </PlayerRow>

          {showHoles && (
            <>
              <SectionRule />
              <OptionSection>
                <GroupLabel>{holesLabel}</GroupLabel>
                <OptionList role="radiogroup" aria-label={holesLabel}>
                  {holesOptions.map((option) => (
                    <OptionRow
                      key={option.value}
                      mode="radio"
                      withIndicator={false}
                      centered
                      label={option.label}
                      selected={option.value === selectedHoles}
                      onClick={() => onHolesSelect(option.value)}
                    />
                  ))}
                </OptionList>
              </OptionSection>
            </>
          )}

          <SectionRule />
          <ReservationCalendar
            activeDate={activeDate}
            selectedDate={selectedDate}
            view={calendarView}
            availability={availability}
            monthViewAvailability
            compactDays
            onDateSelect={onDateSelect}
            onNavigate={onWeekNavigate}
            onViewChange={onCalendarViewChange}
            labels={calendarLabels}
          />

          {showDatePrompt && (
            <EmptyNote>{labels?.selectDateText ?? 'Choose a date to see the tee times.'}</EmptyNote>
          )}

          {(showTimeBands || showTeeTimes) && <SectionRule />}

          {showTimeBands && (
            <TimeslotChipSelector
              layout="fill"
              timeslots={timeBands}
              selectedTimeslot={selectedTimeBand}
              onTimeslotSelect={onTimeBandSelect}
              labels={labels?.timeBandChips}
            />
          )}

          {showTimeBandPrompt && (
            <EmptyNote>
              {labels?.selectTimeBandText ?? 'Choose a time of day to see the tee times.'}
            </EmptyNote>
          )}

          {showTeeTimes &&
            (teeTimes.length > 0 ? (
              <TimeslotChipSelector
                layout="grid"
                timeslots={teeTimes}
                selectedTimeslot={selectedTeeTime}
                onTimeslotSelect={onTeeTimeSelect}
                labels={labels?.teeTimeChips}
              />
            ) : (
              <EmptyNote>
                {labels?.noTeeTimesText ?? 'No tee times are available on this day.'}
              </EmptyNote>
            ))}

          {noteText && <InfoNote text={noteText} icon={noteIcon} />}
        </CardStack>
      </PanelCard>

      {unavailableNotice ? (
        <NotificationBox
          title={unavailableNotice.title}
          subtitle={unavailableNotice.subtitle}
          cta={
            unavailableNotice.ctaLabel && (
              <Button
                type="button"
                variant="primary"
                size="large"
                onClick={onUnavailableCtaClick ?? onBack}
                disabled={!onUnavailableCtaClick && !onBack}
              >
                {unavailableNotice.ctaLabel}
              </Button>
            )
          }
        />
      ) : (
        guests.length > 0 && (
          <PanelCard size="large">
            <CardStack>
              <SectionHeader
                title={labels?.guestsSection?.title ?? 'Guests.'}
                subtitle={labels?.guestsSection?.subtitle}
              />
              {guests.map((guest, guestIndex) => (
                <GuestDetailsPanel
                  key={guest.guestNumber}
                  guestNumber={guest.guestNumber}
                  showHeader={guests.length > 1}
                  showTitle
                  showGender={false}
                  existingGuests={existingGuests}
                  selectedGuestId={guest.selectedGuestId}
                  disabledGuestIds={selectedGuestIds.filter((id) => id !== guest.selectedGuestId)}
                  onExistingGuestSelect={(guestId) => onExistingGuestSelect?.(guestIndex, guestId)}
                  values={guest.values}
                  errors={guest.errors}
                  requiredFields={{
                    title: false,
                    firstName: guest.required,
                    lastName: guest.required,
                  }}
                  onChange={(field, value) => onGuestChange?.(guestIndex, field, value)}
                  titleOptions={titleOptions}
                  labels={labels?.guestDetails}
                />
              ))}
              {onAddGuest && canAddGuest && (
                <AddGuestRow>
                  <Button type="button" variant="outline" onClick={onAddGuest}>
                    {labels?.addGuestLabel ?? 'Add guest'}
                  </Button>
                </AddGuestRow>
              )}
            </CardStack>
          </PanelCard>
        )
      )}

      {errorMessage && (
        <ErrorRow role="alert">
          <ErrorText>{errorMessage}</ErrorText>
        </ErrorRow>
      )}
    </ScrollContainer>
  );
}
